import React from "react";
import { AlertTriangle, X, IdCard, Calendar, User } from "lucide-react";

/**
 * Warning shown when the name and DOB just typed look like a patient who is
 * already in the portal, even though the MRN did not collide.
 *
 * This is advisory. The exact-MRN check still blocks; this one only asks staff
 * to recheck what they typed, since a mistyped MRN or a misspelled name is how
 * a duplicate record gets created in the first place. Two real patients can
 * share a name and a birthday, so "Add Anyway" always stays open.
 *
 * Props:
 *   matches   Array<{ patient, score, reasons }> from findPotentialDuplicates.
 *   entered   { name, dob, mrn } as typed, echoed back in full. The dialog
 *             covers the form, so without this there is nothing to compare
 *             the matches against.
 *   onCancel  Close and return to the form so the entry can be corrected.
 *   onProceed Continue with the add anyway (caller re-confirms).
 *   busy      Disables the buttons while the caller's save is in flight.
 */

// Per-store colours for the location badge, so which record lives where reads
// at a glance instead of needing the label to be parsed.
const SOURCE_STYLES = {
  Referrals: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
  "Referrals (this batch)": "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
  Duplicates: "bg-rose-500/15 border-rose-400/40 text-rose-200",
  "Pre-Screened": "bg-sky-500/15 border-sky-400/40 text-sky-200",
  "Legacy Sheet": "bg-amber-500/15 border-amber-400/40 text-amber-200",
};
const DEFAULT_SOURCE_STYLE = "bg-white/[0.06] border-white/20 text-slate-200";

const BLANK = "not given";

export default function PotentialDuplicateDialog({
  matches = [],
  entered = {},
  onCancel,
  onProceed,
  busy = false,
}) {
  if (!matches.length) return null;

  const typed = [
    { label: "Name", value: entered.name, mono: false },
    { label: "DOB", value: entered.dob, mono: false },
    { label: "MRN", value: entered.mrn, mono: true },
  ].filter((f) => f.value);

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 font-[Manrope]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="potential-duplicate-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="relative w-full max-w-lg max-h-[88vh] flex flex-col rounded-2xl border border-amber-400/30 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] text-slate-100">
        <div className="absolute top-0 inset-x-0 h-[2px] rounded-t-2xl bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 opacity-80 pointer-events-none" />

        {/* Header */}
        <div className="relative px-5 py-4 border-b border-white/10 flex items-start gap-3 shrink-0">
          <div className="shrink-0 w-10 h-10 rounded-full bg-amber-500/15 border border-amber-400/30 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-300" />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              id="potential-duplicate-title"
              className="text-base font-bold text-white leading-tight"
            >
              Possible Duplicate Patient
            </h3>
            <p className="mt-1 text-sm text-slate-300">
              This MRN is new, but {matches.length} existing{" "}
              {matches.length === 1 ? "patient has" : "patients have"} the same
              DOB and a similar name.{" "}
              <span className="font-semibold text-amber-200">
                Recheck the name and MRN you typed.
              </span>
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* What was typed. Tinted indigo so it never reads as one of the
            existing-patient cards below, which are neutral. */}
        {typed.length ? (
          <div className="px-5 pt-4 shrink-0">
            <div className="rounded-xl border border-indigo-400/50 bg-indigo-500/20 px-3 py-2.5">
              {/* Colour tokens here carry NO opacity suffix on purpose. The
                  light-mode rules in index.css match exact class names
                  (`[class~="text-indigo-200"]`), so "text-indigo-200/70" is
                  invisible to them and stays pale on a light background. */}
              <div className="text-[10px] uppercase tracking-wider text-indigo-200 font-bold mb-1.5">
                You entered
              </div>
              <div className="space-y-1">
                {typed.map((f) => (
                  <div key={f.label} className="flex items-baseline justify-between gap-3">
                    <span className="text-xs text-indigo-200 shrink-0">
                      {f.label}
                    </span>
                    <span
                      className={`text-sm text-white font-medium truncate ${
                        f.mono ? "font-mono tracking-wider" : ""
                      }`}
                    >
                      {f.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* Matches */}
        <div className="relative overflow-y-auto flex-1 px-5 py-4 space-y-2.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
            Already in the portal
          </div>
          {matches.map(({ patient, reasons }, i) => (
            <div
              key={patient.id || patient.token || i}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <User className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span className="text-sm font-semibold text-white truncate">
                    {patient.fullName || patient.name || "Unknown name"}
                  </span>
                </div>
                {patient.sourceLabel ? (
                  <span
                    className={`shrink-0 text-[10px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 ${
                      SOURCE_STYLES[patient.sourceLabel] || DEFAULT_SOURCE_STYLE
                    }`}
                  >
                    {patient.sourceLabel}
                  </span>
                ) : null}
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Calendar className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span className="text-slate-400 text-xs">DOB</span>
                  <span className="text-slate-100 font-medium truncate">
                    {patient.dob || BLANK}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 min-w-0">
                  <IdCard className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span className="text-slate-400 text-xs">MRN</span>
                  <span className="text-slate-100 font-mono tracking-wider truncate">
                    {patient.mrn || BLANK}
                  </span>
                </div>
              </div>

              {reasons && reasons.length ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {reasons.map((r) => (
                    <span
                      key={r}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/25 text-amber-200"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 backdrop-blur-xl flex items-center justify-end gap-2 shrink-0 rounded-b-2xl">
          <button
            onClick={onProceed}
            disabled={busy}
            className="px-3 py-2.5 rounded-xl bg-rose-500/15 border border-rose-400/40 text-xs font-medium text-rose-300 hover:bg-rose-500/25 transition disabled:opacity-40"
          >
            Add Anyway
          </button>
          <button
            onClick={onCancel}
            disabled={busy}
            autoFocus
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] transition disabled:opacity-40"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}
